import React, { useEffect, useState, useCallback } from 'react';
import { api, extractApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Mail, MessageCircleQuestion, Bug, Lightbulb, Sparkles, MoreHorizontal } from 'lucide-react';

const CATEGORY_META = {
  suggestion: { label: 'Suggestion', icon: MessageCircleQuestion, color: 'bg-blue-500/10 text-blue-700 border-blue-300' },
  feature_request: { label: 'Feature Request', icon: Sparkles, color: 'bg-purple-500/10 text-purple-700 border-purple-300' },
  bug: { label: 'Bug', icon: Bug, color: 'bg-red-500/10 text-red-700 border-red-300' },
  other: { label: 'Other', icon: Lightbulb, color: 'bg-amber-500/10 text-amber-800 border-amber-300' },
};

const STATUS_META = {
  open: { label: 'Open', color: 'bg-red-500/10 text-red-700 border-red-300' },
  in_progress: { label: 'In progress', color: 'bg-amber-500/10 text-amber-700 border-amber-300' },
  resolved: { label: 'Resolved', color: 'bg-emerald-500/10 text-emerald-700 border-emerald-300' },
  closed: { label: 'Closed', color: 'bg-secondary text-muted-foreground border-border' },
};

const STATUSES = ['open', 'in_progress', 'resolved', 'closed'];

const AdminFeedback = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0 });
  const [tab, setTab] = useState('open');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState({ open: false, item: null });
  const [replyModal, setReplyModal] = useState({ open: false, item: null, subject: '', body: '' });
  const [replyBusy, setReplyBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: tab, page: 1, limit: 100 };
      if (category) params.category = category;
      const { data } = await api.get('/admin/feedback', { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setStatusCounts(data.status_counts || {});
    } catch (e) {
      toast.error(extractApiError(e, 'Failed to load feedback'));
    } finally { setLoading(false); }
  }, [tab, category]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/feedback/${id}`, { status });
      toast.success('Status updated');
      // If we changed the visible item's status, refresh both the list and
      // the currently-open detail dialog so the badge updates instantly.
      if (detail.item?.id === id) setDetail(d => ({ ...d, item: { ...d.item, status } }));
      load();
    } catch (e) { toast.error(extractApiError(e, 'Update failed')); }
  };

  const saveAdminNote = async (id, note) => {
    try {
      const { data } = await api.patch(`/admin/feedback/${id}`, { admin_note: note });
      toast.success('Note saved');
      if (detail.item?.id === id) setDetail(d => ({ ...d, item: data.item }));
      load();
    } catch (e) { toast.error(extractApiError(e, 'Save failed')); }
  };

  const sendReply = async () => {
    const s = replyModal.subject.trim();
    const b = replyModal.body.trim();
    if (s.length < 3 || b.length < 10) { toast.error('Subject and body are too short'); return; }
    setReplyBusy(true);
    try {
      await api.post(`/admin/feedback/${replyModal.item.id}/reply`, { subject: s, body: b });
      toast.success('Reply queued to send');
      setReplyModal({ open: false, item: null, subject: '', body: '' });
      // Auto-move to resolved unless already closed/resolved
      if (['open'].includes(replyModal.item.status)) {
        await updateStatus(replyModal.item.id, 'in_progress');
      }
    } catch (e) { toast.error(extractApiError(e, 'Reply failed')); }
    finally { setReplyBusy(false); }
  };

  const openReply = (item) => {
    setReplyModal({
      open: true,
      item,
      subject: `Re: ${item.subject}`,
      body: `Hi ${item.user_name || 'there'},\n\nThanks for reaching out about "${item.subject}".\n\n\n\n— IndusFact team`,
    });
  };

  return (
    <div className='space-y-4' data-testid='admin-feedback-page'>
      <div>
        <h1 className='text-2xl font-semibold'>User Feedback</h1>
        <p className='text-sm text-muted-foreground'>Suggestions, feature requests, bugs and general feedback from users.</p>
      </div>

      <Card className='p-3'>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {STATUSES.map(s => (
              <TabsTrigger key={s} value={s} data-testid={`fb-tab-${s}`}>
                {STATUS_META[s].label} ({statusCounts[s] || 0})
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className='mt-3 flex items-center gap-2 flex-wrap'>
          <Label className='text-xs text-muted-foreground'>Category:</Label>
          <Select value={category || 'all'} onValueChange={(v) => setCategory(v === 'all' ? '' : v)}>
            <SelectTrigger className='w-52' data-testid='fb-category-filter'><SelectValue placeholder='All' /></SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All categories</SelectItem>
              {Object.entries(CATEGORY_META).map(([k, m]) => (
                <SelectItem key={k} value={k}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className='text-xs text-muted-foreground ml-auto'>{loading ? 'Loading…' : `${total} item${total === 1 ? '' : 's'}`}</span>
        </div>
      </Card>

      {items.length === 0 && !loading ? (
        <Card className='p-8 text-center text-sm text-muted-foreground' data-testid='fb-empty'>
          No feedback in this bucket yet.
        </Card>
      ) : (
        <div className='space-y-2'>
          {items.map(it => {
            const cat = CATEGORY_META[it.category] || CATEGORY_META.other;
            const stat = STATUS_META[it.status] || STATUS_META.open;
            const CatIcon = cat.icon;
            return (
              <Card key={it.id} className='p-4 hover:bg-secondary/30 cursor-pointer' onClick={() => setDetail({ open: true, item: it })} data-testid={`fb-item-${it.id}`}>
                <div className='flex items-start justify-between gap-3 flex-wrap'>
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-1.5 flex-wrap'>
                      <Badge variant='outline' className={`text-[10px] ${cat.color}`}><CatIcon className='h-3 w-3 mr-1' /> {cat.label}</Badge>
                      <Badge variant='outline' className={`text-[10px] ${stat.color}`}>{stat.label}</Badge>
                      {it.reply_count > 0 && <Badge variant='outline' className='text-[10px]'><Mail className='h-3 w-3 mr-1' /> {it.reply_count} repl{it.reply_count === 1 ? 'y' : 'ies'}</Badge>}
                    </div>
                    <div className='mt-1 font-medium text-sm'>{it.subject}</div>
                    <div className='text-xs text-muted-foreground line-clamp-2 mt-0.5'>{it.message}</div>
                    <div className='text-[11px] text-muted-foreground mt-1'>
                      {it.user_name || 'Anonymous'}{it.user_email ? ` · ${it.user_email}` : ''} · {new Date(it.created_at).toLocaleString('en-IN')}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={detail.open} onOpenChange={(o) => !o && setDetail({ open: false, item: null })}>
        <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto' data-testid='fb-detail-dialog'>
          {detail.item && (
            <>
              <DialogHeader>
                <DialogTitle>{detail.item.subject}</DialogTitle>
                <DialogDescription>
                  {detail.item.user_name || 'Anonymous'}{detail.item.user_email ? ` · ${detail.item.user_email}` : ''} · {new Date(detail.item.created_at).toLocaleString('en-IN')}
                </DialogDescription>
              </DialogHeader>
              <div className='space-y-3 text-sm'>
                <div className='flex items-center gap-1.5 flex-wrap'>
                  <Badge variant='outline' className={`text-[10px] ${CATEGORY_META[detail.item.category]?.color}`}>{CATEGORY_META[detail.item.category]?.label}</Badge>
                  <Select value={detail.item.status} onValueChange={(v) => updateStatus(detail.item.id, v)}>
                    <SelectTrigger className='h-8 w-40 text-xs' data-testid='fb-detail-status-select'><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {detail.item.page_url && <span className='text-[11px] text-muted-foreground truncate'>from {detail.item.page_url}</span>}
                </div>
                <div className='rounded-md border bg-secondary/30 p-3 whitespace-pre-wrap'>{detail.item.message}</div>
                <div>
                  <Label className='text-xs mb-1 block'>Admin note (private)</Label>
                  <Textarea
                    rows={3}
                    defaultValue={detail.item.admin_note || ''}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v !== (detail.item.admin_note || '')) saveAdminNote(detail.item.id, v);
                    }}
                    placeholder='Internal notes on this feedback (auto-save on blur)'
                    data-testid='fb-detail-admin-note'
                  />
                </div>
                {Array.isArray(detail.item.replies) && detail.item.replies.length > 0 && (
                  <div className='space-y-2'>
                    <Label className='text-xs'>Sent replies</Label>
                    {detail.item.replies.map(r => (
                      <div key={r.id} className='rounded-md border bg-emerald-50/50 dark:bg-emerald-950/20 p-3'>
                        <div className='text-xs text-muted-foreground'>{r.sent_by_name} · {new Date(r.ts).toLocaleString('en-IN')}</div>
                        <div className='font-medium mt-1'>{r.subject}</div>
                        <div className='whitespace-pre-wrap text-sm mt-1'>{r.body}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter className='gap-2'>
                <Button variant='outline' onClick={() => setDetail({ open: false, item: null })}>Close</Button>
                {detail.item.user_email && (
                  <Button onClick={() => openReply(detail.item)} data-testid='fb-detail-reply-btn'>
                    <Mail className='h-4 w-4 mr-1.5' /> Reply by email
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reply dialog */}
      <Dialog open={replyModal.open} onOpenChange={(o) => !o && setReplyModal({ open: false, item: null, subject: '', body: '' })}>
        <DialogContent data-testid='fb-reply-dialog' className='max-w-2xl' onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Reply by email</DialogTitle>
            <DialogDescription>
              To: <strong>{replyModal.item?.user_email}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            <div>
              <Label className='text-xs'>Subject</Label>
              <Input value={replyModal.subject} onChange={(e) => setReplyModal(m => ({ ...m, subject: e.target.value }))} maxLength={200} data-testid='fb-reply-subject' />
            </div>
            <div>
              <Label className='text-xs'>Body</Label>
              <Textarea rows={10} value={replyModal.body} onChange={(e) => setReplyModal(m => ({ ...m, body: e.target.value }))} maxLength={6000} data-testid='fb-reply-body' />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setReplyModal({ open: false, item: null, subject: '', body: '' })} disabled={replyBusy}>Cancel</Button>
            <Button onClick={sendReply} disabled={replyBusy} data-testid='fb-reply-send'>{replyBusy ? 'Sending…' : 'Send reply'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFeedback;
